import Color from '@/Enums/Color.enum';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import { useAppSelector } from '@/store/hooks';
import { selectFootTrafficOnAirOffAir } from '@/store/selectors/attribution/footTraffic.selectors';
import { Box, Skeleton, Typography } from '@mui/material';
import { useMemo } from 'react';
import GraphCard from './BaseCard/GraphCard';

const label = 'On-Air vs. Off-Air';

interface RowProps {
  title: string;
  value: number | null;
  scale: number;
  color: string;
  loading: boolean;
}

const Row = ({ title, value, scale, color, loading }: RowProps) => {
  const width = value && scale ? Math.max(2, Math.round((value / scale) * 100)) : 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
      <Typography sx={{ width: 80, fontSize: 13, color: 'text.secondary' }}>{title}</Typography>
      <Box sx={{ flex: 1, position: 'relative', height: 28, bgcolor: '#f3f4f6', borderRadius: '4px' }}>
        {loading ? (
          <Skeleton variant='rectangular' sx={{ position: 'absolute', inset: 0, borderRadius: '4px' }} />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: `${width}%`,
              bgcolor: color,
              borderRadius: '4px',
              transition: 'width 200ms ease',
            }}
          />
        )}
      </Box>
      <Typography sx={{ width: 90, textAlign: 'right', fontSize: 14, fontWeight: 600 }}>
        {value == null ? '—' : numberToUsFormat(Math.round(value))}
      </Typography>
    </Box>
  );
};

const OnAirOffAirBar = () => {
  const { loading, onAir, offAir } = useAppSelector(selectFootTrafficOnAirOffAir);

  const scale = Math.max(onAir ?? 0, offAir ?? 0, 1);
  const noData = !loading && onAir == null && offAir == null;

  const liftCopy = useMemo(() => {
    if (onAir == null || offAir == null || offAir === 0) return null;
    const lift = (onAir - offAir) / offAir;
    const pct = Math.abs(lift * 100);
    const direction = lift >= 0 ? 'higher' : 'lower';
    return `On-air weeks averaged ${pct.toFixed(1)}% ${direction} visits than off-air weeks.`;
  }, [onAir, offAir]);

  return (
    <GraphCard label={label} loading={loading} noData={noData}>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
        Average weekly visits during days with campaign impressions vs. days without.
      </Typography>
      <Row title='On-Air' value={onAir} scale={scale} color={Color.Primary} loading={loading} />
      <Row title='Off-Air' value={offAir} scale={scale} color={Color.Grey} loading={loading} />
      {liftCopy && (
        <Typography sx={{ mt: 1, fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>{liftCopy}</Typography>
      )}
    </GraphCard>
  );
};

export default OnAirOffAirBar;
